import _ from 'lodash';
import { ChevronRightIcon, HomeIcon, PanelLeftIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { twMerge } from 'tailwind-merge';
import sidebarStore from '../stores/sidebarStore';
import { CommonLink } from '../views/common/CommonLink';
import ListeningUserProfile from './settings/ListeningUserProfile';
import ListeningTextLogo from './ListeningTextLogo';

// 사이드바 너비 상수
const SIDEBAR_COLLAPSED_WIDTH = 52; // 접힌 상태 너비 (px)
const SIDEBAR_EXPANDED_WIDTH = 290; // 펼친 상태 너비 (px)
const SIDEBAR_LOGO_AREA_HEIGHT = 48; // 로고 영역 높이 (px)
const BREAKPOINT_WIDTH = 1280; // 브레이크포인트 너비 (px)

/**
 * 더리스닝 전용 사이드바
 *
 * 넓은 화면 (데스크탑):
 * - 접힘: 아이콘만 보임
 * - 펼침: 아이콘 + 텍스트
 *
 * 좁은 화면 (모바일):
 * - 접힘: 토글 버튼만 보임
 * - 펼침: 오버레이로 아이콘 + 텍스트
 */
const ListeningSidebar = observer(() => {
    const isExpanded = sidebarStore.isOpen;
    const width = isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

    const location = useLocation();
    const prevLocation = useRef(location);

    /**
     * 마운트 시 로컬 스토리지에서 상태 불러오기
     */
    useEffect(() => {
        sidebarStore.loadState();
    }, []);

    /**
     * 라우팅이 바뀌면 사이드바를 닫음
     */
    useEffect(() => {
        if (prevLocation.current !== location) {
            // BREAKPOINT_WIDTH 미만에서만 적용
            if (window.innerWidth < BREAKPOINT_WIDTH) {
                sidebarStore.closeSidebar();
            }
        }
        prevLocation.current = location;
    }, [location]);

    /**
     * 사이드바가 열려있을 때, 윈도우 사이즈가 BREAKPOINT_WIDTH 이 넘으면 닫아줌
     */
    useEffect(() => {
        if (!sidebarStore.isOpen) {
            return;
        }

        const debouncedHandleResize = _.debounce(() => {
            // css 로 숨김 처리하지만 레이어까지 완전히 닫아주기 위해 추가로 붙여줌
            if (window.innerWidth < BREAKPOINT_WIDTH) {
                sidebarStore.closeSidebar();
            }
        }, 100);

        window.addEventListener('resize', debouncedHandleResize);

        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, [isExpanded]);

    return (
        <>
            <div
                className={twMerge(
                    // 공통
                    'pointer-events-none top-0 left-0 z-(--z-index-sidebar)',
                    'select-none', // 텍스트 선택 못하게 처리
                    // 모바일
                    'fixed',
                    // 데스크탑 (sticky 로 해줘야 sidebar/main 이 flex 로 위치를 잡음)
                    'xl:sticky',
                )}
            >
                <nav
                    className={twMerge('h-dvh transition-[width] duration-200 ease-in-out')}
                    style={{
                        width,
                    }}
                >
                    {/* 상단: 토글 버튼 + 타이틀 */}
                    <div
                        className={twMerge(
                            'pointer-events-auto absolute top-0 right-0 left-0 flex h-(--listening-header-height) items-center px-2',
                            isExpanded ? 'justify-between' : 'justify-center',
                        )}
                        style={{
                            width: isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
                            height: SIDEBAR_LOGO_AREA_HEIGHT,
                        }}
                    >
                        <div className={twMerge(isExpanded ? 'block' : 'hidden')}>
                            <SidebarLogo />
                        </div>
                        <SidebarToggleButton isExpanded={isExpanded} />
                    </div>
                    <div
                        className={twMerge(
                            // 공통
                            'bg-base-100 border-base-content/10 h-full border-r',

                            // 모바일에선 기본 숨김
                            'pointer-events-none opacity-0 shadow-xl',
                            // 데스크탑에선 보임
                            'xl:pointer-events-auto xl:block xl:opacity-100 xl:shadow-none',

                            // 펼친 상태에선 보임
                            isExpanded && 'pointer-events-auto opacity-100',
                        )}
                        style={{
                            paddingTop: SIDEBAR_LOGO_AREA_HEIGHT,
                        }}
                    >
                        <SidebarContent isExpanded={isExpanded} />
                    </div>
                </nav>
            </div>
            {isExpanded && (
                <div
                    // 모바일에서 배경 오버레이
                    className={twMerge(
                        'fixed inset-0 bg-transparent',
                        // 데스크탑(펼친 상태): 숨김
                        'xl:hidden',
                    )}
                    style={{
                        zIndex: 'calc(var(--z-index-sidebar) - 1)',
                    }}
                    onClick={() => {
                        sidebarStore.closeSidebar();
                    }}
                />
            )}
        </>
    );
});

/**
 * 사이드바 컨텐츠
 */
const SidebarContent = observer(({ isExpanded }: { isExpanded: boolean }) => {
    return (
        <div className={twMerge('flex h-full flex-col')}>
            {/* 메뉴 아이템들 */}
            <div className={twMerge('mx-2 mt-4 flex flex-1 flex-col gap-1')}>
                <SidebarItem
                    to="/dashboard"
                    icon={<HomeIcon className="size-4.5 stroke-[1.5]" />}
                    label="대시보드"
                    isExpanded={isExpanded}
                />
            </div>

            {/* 하단: 설정 */}
            <div
                className={twMerge(
                    'border-base-content/10 mt-auto',
                    isExpanded ? 'border-t' : 'border-0',
                )}
            >
                <CommonLink
                    to="/settings"
                    className={twMerge(
                        'hover:bg-base-content/10 px-2 pt-3 pb-4',
                        'flex flex-row',
                        !isExpanded && 'justify-start',
                        isExpanded && 'justify-between',
                    )}
                    preventScrollReset={false}
                >
                    <ListeningUserProfile className="w-full" isShowName={isExpanded} />
                    {isExpanded && (
                        <div className="flex items-center justify-center">
                            <ChevronRightIcon className="text-base-content/60 mr-0.5 size-5 stroke-1" />
                        </div>
                    )}
                </CommonLink>
            </div>
        </div>
    );
});

const SidebarLogo = observer(() => {
    return (
        <CommonLink
            to="/dashboard"
            className={twMerge('text-base-content flex h-8 w-20 items-center px-2')}
        >
            <ListeningTextLogo />
        </CommonLink>
    );
});

/**
 * 사이드바 토글 버튼
 */
const SidebarToggleButton = observer(({ isExpanded }: { isExpanded: boolean }) => {
    return (
        <button
            className={twMerge(
                'btn btn-ghost btn-circle btn-sm text-base-content/60',
                !isExpanded && 'bg-base-100 shadow-xs',
                'lg:bg-transparent lg:shadow-none',
            )}
            onClick={() => {
                if (sidebarStore.isOpen) {
                    sidebarStore.closeSidebar();
                } else {
                    sidebarStore.openSidebar();
                }
            }}
        >
            <PanelLeftIcon className="size-4.5 stroke-[1.5]" />
        </button>
    );
});

/**
 * 사이드바 아이템
 * - isExpanded: 펼침 상태일 때 아이콘 + 텍스트, 접힘 상태일 때 아이콘만
 */
const SidebarItem = ({
    to,
    icon,
    label,
    isExpanded,
}: {
    to: string;
    icon: React.ReactNode;
    label: string;
    isExpanded: boolean;
}) => {
    const location = useLocation();

    const isActive = useMemo(() => {
        return location.pathname === to;
    }, [location.pathname, to]);

    return (
        <CommonLink
            to={to}
            className={twMerge(
                'flex h-9 items-center gap-2.5 rounded-lg px-2',
                'hover:bg-base-content/10',
                isExpanded && 'justify-start',
                !isExpanded && 'btn-square',
                isActive && 'bg-base-content/10',
            )}
            preventScrollReset={false}
        >
            <span
                className={twMerge(
                    'text-base-content/70 shrink-0',
                    isActive && 'text-base-content',
                )}
            >
                {icon}
            </span>
            {isExpanded && (
                <span
                    className={twMerge(
                        'text-base-content/70 text-sm whitespace-nowrap',
                        isActive && 'text-base-content font-medium',
                    )}
                >
                    {label}
                </span>
            )}
        </CommonLink>
    );
};

export default ListeningSidebar;
